/**
 * OperatorProvisioningWizard — internal super-admin blueprint generator.
 *
 * Generate-only: collects a new operator's setup across 10 steps and produces
 * deployment artifacts (env files + DNS/Twilio/Resend/first-admin checklists +
 * readiness summary) for a SEPARATE Dispatch OS deployment. It NEVER creates
 * real accounts or mutates this running system. Secret fields are held in
 * component state only and are NEVER sent to the save endpoint.
 *
 * Surfaced only behind REACT_APP_SHOW_LAUNCH_TOOLS (see tabsConfig.js).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Server, Building2, FileText, UserCog, Headset, Phone, Mail, Palette,
  DollarSign, CreditCard, Rocket, Plus, Trash2, ChevronRight, Save, Loader2,
  Copy, Download, ShieldAlert, CheckCircle2, ArrowLeft, KeyRound, Bot, HelpCircle,
} from "lucide-react";
import {
  generateBackendEnv, generateFrontendEnv, generateDnsChecklist,
  generateTwilioWebhookChecklist, generateResendChecklist,
  generateFirstAdminChecklist, generateReadinessSummary, generateProvisioningPack,
  generateStripeChecklist, generateGoLiveTestPlan, evaluateGroups,
  evaluateReadiness, slugify, downloadText,
} from "./operatorProvisioningOutput";
import { OperatorSetupAssistant } from "./OperatorSetupAssistant";

const T = "text";
// Step + field schema. `secret: true` fields are never persisted.
const STEPS = [
  {
    id: "company", label: "Company details", icon: Building2,
    fields: [
      { k: "deployment_domain", label: "Deployment domain", hint: "Where this operator will live, e.g. dispatch.acmecabs.com", ph: "dispatch.acmecabs.com" },
      { k: "company_name", label: "Company name", ph: "Acme Cabs Ltd" },
      { k: "legal_name", label: "Legal entity (optional)", ph: "Acme Holdings Limited" },
      { k: "company_email", label: "Main contact email", type: "email", ph: "hello@acmecabs.com" },
      { k: "company_phone", label: "Main contact phone", ph: "+44 1256 000000" },
      { k: "website", label: "Website (optional)", ph: "https://acmecabs.com" },
      { k: "base_address", label: "Base address", ph: "1 High Street, Townville" },
      { k: "base_postcode", label: "Base postcode", ph: "RG21 7BB" },
      { k: "base_lat", label: "Base latitude", type: "number", ph: "51.2667" },
      { k: "base_lng", label: "Base longitude", type: "number", ph: "-1.0876" },
    ],
  },
  {
    id: "licence", label: "Operator licence", icon: FileText,
    fields: [
      { k: "licensing_authority", label: "Licensing authority", ph: "Basingstoke & Deane Borough Council" },
      { k: "licence_number", label: "Licence number", ph: "PHO/00/12345" },
      { k: "licence_expiry", label: "Licence expiry", type: "date" },
      { k: "service_area", label: "Service area (optional)", type: "textarea", ph: "Towns and districts covered…" },
    ],
  },
  {
    id: "owner_admin", label: "Owner / admin account", icon: UserCog,
    fields: [
      { k: "admin_name", label: "Admin full name", ph: "Jane Owner" },
      { k: "admin_email", label: "Admin email", type: "email", ph: "owner@acmecabs.com" },
      { k: "admin_initial_password", label: "Initial password", secret: true, hint: "Not stored — used only in the generated checklist", type: "password" },
    ],
  },
  {
    id: "dispatcher", label: "Dispatcher account", icon: Headset,
    fields: [
      { k: "dispatcher_name", label: "Dispatcher full name", ph: "Sam Dispatch" },
      { k: "dispatcher_email", label: "Dispatcher email", type: "email", ph: "dispatch@acmecabs.com" },
      { k: "dispatcher_initial_password", label: "Initial password", secret: true, hint: "Not stored", type: "password" },
    ],
  },
  {
    id: "twilio", label: "Twilio credentials", icon: Phone,
    fields: [
      { k: "twilio_account_sid", label: "Account SID", ph: "ACxxxxxxxx" },
      { k: "twilio_auth_token", label: "Auth token", secret: true, hint: "Not stored — rendered into env output only", type: "password" },
      { k: "twilio_sms_number", label: "SMS number", ph: "+44..." },
      { k: "twilio_voice_mask_number", label: "Voice mask number", ph: "+44..." },
      { k: "twilio_public_booking_number", label: "Public booking number", ph: "+44..." },
      { k: "twilio_dispatcher_number", label: "Dispatcher number (optional)", ph: "+44..." },
      { k: "twilio_whatsapp_from", label: "WhatsApp from (optional)", ph: "whatsapp:+44..." },
      { k: "twilio_sms_enabled", label: "Enable SMS", type: "check" },
      { k: "twilio_whatsapp_enabled", label: "Enable WhatsApp", type: "check" },
    ],
  },
  {
    id: "email", label: "Resend / email", icon: Mail,
    fields: [
      { k: "resend_api_key", label: "Resend API key", secret: true, hint: "Not stored", type: "password" },
      { k: "email_from_name", label: "From name", ph: "Acme Cabs" },
      { k: "resend_from_email", label: "From email", type: "email", ph: "no-reply@mail.acmecabs.com" },
      { k: "resend_domain", label: "Email sending domain", ph: "mail.acmecabs.com" },
      { k: "support_email", label: "Support (Reply-To) email", type: "email", ph: "support@acmecabs.com" },
    ],
  },
  {
    id: "branding", label: "Branding / logo", icon: Palette,
    fields: [
      { k: "brand_logo_url", label: "Logo URL", ph: "https://cdn.acmecabs.com/logo.png" },
      { k: "brand_tagline", label: "Tagline (optional)", ph: "Always on the road for you." },
      { k: "brand_primary_color", label: "Primary brand colour", ph: "#002FA7" },
    ],
  },
  {
    id: "fares", label: "Fare settings", icon: DollarSign,
    fields: [
      { k: "currency", label: "Currency", ph: "GBP" },
      { k: "base_fare", label: "Base fare", type: "number", ph: "3.50" },
      { k: "per_mile", label: "Per mile", type: "number", ph: "2.20" },
      { k: "per_minute", label: "Per minute", type: "number", ph: "0.30" },
      { k: "minimum_fare", label: "Minimum fare", type: "number", ph: "5.00" },
      { k: "booking_fee", label: "Booking fee", type: "number", ph: "0.00" },
      { k: "vat_percent", label: "VAT %", type: "number", ph: "0" },
    ],
  },
  {
    id: "payments", label: "Stripe / payments", icon: CreditCard,
    fields: [
      { k: "stripe_mode", label: "Mode", type: "select", options: ["test", "live"] },
      { k: "stripe_publishable_key", label: "Publishable key", ph: "pk_live_..." },
      { k: "stripe_secret_key", label: "Secret key", secret: true, hint: "Not stored", type: "password" },
      { k: "stripe_webhook_secret", label: "Webhook signing secret", secret: true, hint: "Not stored", type: "password" },
      { k: "payments_enabled", label: "Enable card payments", type: "check" },
    ],
  },
  { id: "review", label: "Readiness & output", icon: Rocket, fields: [] },
];

const inputCls = "w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]";

export const OperatorProvisioningWizard = () => {
  const [list, setList] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({});
  const [secrets, setSecrets] = useState({});
  const [stepId, setStepId] = useState("company");
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [assistantOpen, setAssistantOpen] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/provisioning");
      setList(data.operators || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load operators");
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openDraft = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/provisioning/${id}`);
      setDraft(data);
      setForm(data.data || {});
      setSecrets({});
      setActiveId(id);
      setStepId("company");
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to open");
    } finally {
      setBusy(false);
    }
  };

  const createDraft = async () => {
    if (!newName.trim()) { toast.message("Give the operator a name first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/provisioning", { name: newName.trim() });
      setNewName("");
      await loadList();
      await openDraft(data.id);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this operator setup? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/provisioning/${id}`);
      toast.success("Deleted");
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to delete");
    }
  };

  const onField = (f) => (e) => {
    const raw = e?.target ? (f.type === "check" ? e.target.checked : e.target.value) : e;
    if (f.secret) {
      setSecrets((s) => ({ ...s, [f.k]: raw }));
    } else {
      setForm((p) => ({ ...p, [f.k]: raw }));
    }
  };

  const saveStep = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      // Only NON-secret fields are sent. Secrets never leave the browser.
      const completed = Array.from(new Set([...(draft?.completed_steps || []), stepId]));
      const { data } = await api.put(`/admin/provisioning/${activeId}`, {
        data: form, completed_steps: completed,
      });
      setDraft(data);
      setForm(data.data || {});
      toast.success("Saved");
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const stepIndex = STEPS.findIndex((s) => s.id === stepId);
  const activeStep = STEPS[stepIndex];
  const readiness = useMemo(() => evaluateReadiness(form), [form]);

  // ── Draft list (no operator open) ───────────────────────────────────────
  if (!activeId) {
    return (
      <div className="bg-white" data-testid="provisioning-list">
        <div className="px-4 md:px-6 py-4 border-b border-border bg-[#FAFAF7]">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            <h2 className="font-heading text-2xl font-black tracking-tight" data-testid="provisioning-title">
              Operator Provisioning
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Internal tool. Capture a new operator's setup and generate the deployment
            blueprint (env files + DNS / Twilio / Resend / first-admin checklists) for a
            <strong> separate</strong> Dispatch OS deployment. Nothing here changes this system.
          </p>
          <div className="mt-3 flex items-start gap-2 border border-[#FCD34D] bg-[#FFFBEB] text-[#92400E] px-3 py-2 text-xs" data-testid="provisioning-secret-note">
            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Secrets (Twilio token, Resend / Stripe keys, passwords) are <strong>never stored</strong> — they live only in this browser session and are woven into the generated output.</span>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          <div className="flex gap-2 max-w-md" data-testid="provisioning-create-row">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New operator name e.g. Acme Cabs" className={inputCls} data-testid="provisioning-new-name" />
            <Button onClick={createDraft} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white flex items-center gap-1.5" data-testid="provisioning-create-btn">
              <Plus className="w-4 h-4" /> New
            </Button>
          </div>

          {list === null ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="provisioning-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground border border-dashed border-border" data-testid="provisioning-empty">No operator setups yet. Create one above.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="provisioning-cards">
              {list.map((op) => (
                <div key={op.id} className="border border-border p-4 hover:shadow-md transition-shadow" data-testid={`provisioning-card-${op.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-black text-lg truncate">{op.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{op.deployment_domain || op.company_name || "—"}</div>
                    </div>
                    <button onClick={() => deleteDraft(op.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`provisioning-delete-${op.id}`} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-secondary border border-border overflow-hidden">
                      <div className="h-full bg-[#0A0A0A]" style={{ width: `${op.percent}%` }} />
                    </div>
                    <span className="label-mini text-muted-foreground font-mono-tab">{op.steps_done}/{op.steps_total}</span>
                  </div>
                  <Button onClick={() => openDraft(op.id)} variant="outline" size="sm" className="mt-3 w-full flex items-center justify-center gap-1.5" data-testid={`provisioning-open-${op.id}`}>
                    Open <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Wizard (operator open) ──────────────────────────────────────────────
  return (
    <div className="bg-white" data-testid="provisioning-wizard">
      <div className="px-4 md:px-6 py-4 border-b border-border bg-[#FAFAF7] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => { setActiveId(null); setDraft(null); loadList(); }} data-testid="provisioning-back" className="flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> All operators
          </Button>
          <h2 className="font-heading text-xl font-black tracking-tight truncate" data-testid="provisioning-draft-name">{draft?.name}</h2>
        </div>
        <span className="label-mini text-muted-foreground whitespace-nowrap">Config {readiness.percent}%</span>
      </div>

      <div className="px-4 md:px-6 py-2 border-b border-border bg-white flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setAssistantOpen(true)} className="flex items-center gap-1.5" data-testid="open-setup-assistant">
          <Bot className="w-4 h-4 text-[#FFC107]" /> Setup Assistant
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
        <nav className="border-r border-border" data-testid="provisioning-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = (draft?.completed_steps || []).includes(s.id);
            const active = s.id === stepId;
            return (
              <button key={s.id} onClick={() => setStepId(s.id)} className={`w-full text-left px-4 py-3 border-b border-border flex items-center gap-2 transition-colors ${active ? "bg-[#FFFBEB] border-l-4 border-l-[#FFC107]" : "hover:bg-secondary border-l-4 border-l-transparent"}`} data-testid={`provisioning-step-${s.id}`}>
                <span className="font-mono-tab text-[11px] text-muted-foreground w-4">{i + 1}</span>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-sm font-semibold truncate">{s.label}</span>
                {done && <CheckCircle2 className="w-4 h-4 text-[#10B981]" />}
              </button>
            );
          })}
        </nav>

        <section className="p-4 md:p-6 min-h-[420px]" data-testid={`provisioning-panel-${stepId}`}>
          <h3 className="font-heading text-xl font-black mb-4">{activeStep.label}</h3>

          {stepId === "review" ? (
            <OutputPanel data={form} secrets={secrets} readiness={readiness} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {activeStep.fields.map((f) => (
                  <FieldRow key={f.k} f={f} value={f.secret ? (secrets[f.k] || "") : (form[f.k] ?? "")} onChange={onField(f)} />
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                <Button variant="ghost" size="sm" disabled={stepIndex === 0} onClick={() => setStepId(STEPS[stepIndex - 1].id)} data-testid="provisioning-prev">Previous</Button>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAssistantOpen(true)} className="flex items-center gap-1.5 text-[#002FA7]" data-testid="ask-about-step">
                    <HelpCircle className="w-3.5 h-3.5" /> Ask about this step
                  </Button>
                  <Button onClick={saveStep} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white flex items-center gap-1.5" data-testid="provisioning-save-step">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save step
                  </Button>
                  <Button size="sm" onClick={() => setStepId(STEPS[stepIndex + 1].id)} className="flex items-center gap-1.5" variant="outline" data-testid="provisioning-next">
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <OperatorSetupAssistant stepId={stepId} open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
};

const FieldRow = ({ f, value, onChange }) => {
  const isFull = f.type === "textarea";
  return (
    <label className={`block ${isFull ? "md:col-span-2" : ""}`} data-testid={`provisioning-field-${f.k}`}>
      <Label className="label-mini text-muted-foreground mb-1 flex items-center gap-1">
        {f.secret && <KeyRound className="w-3 h-3 text-[#92400E]" />}{f.label}
      </Label>
      {f.type === "textarea" ? (
        <textarea value={value} onChange={onChange} rows={3} placeholder={f.ph} className={`${inputCls} resize-none`} data-testid={`provisioning-input-${f.k}`} />
      ) : f.type === "check" ? (
        <label className="flex items-center gap-2 text-sm cursor-pointer mt-1">
          <input type="checkbox" checked={!!value} onChange={onChange} data-testid={`provisioning-input-${f.k}`} /> {f.label}
        </label>
      ) : f.type === "select" ? (
        <select value={value} onChange={onChange} className={inputCls} data-testid={`provisioning-input-${f.k}`}>
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input type={f.type || T} value={value} onChange={onChange} placeholder={f.ph} className={inputCls} autoComplete={f.secret ? "new-password" : "off"} data-testid={`provisioning-input-${f.k}`} />
      )}
      {f.hint && <div className="text-[11px] text-muted-foreground mt-1">{f.hint}</div>}
    </label>
  );
};

const OUTPUTS = [
  { key: "backend_env", label: "backend/.env.production", file: "backend.env.production", gen: generateBackendEnv },
  { key: "frontend_env", label: "frontend/.env", file: "frontend.env", gen: (d) => generateFrontendEnv(d) },
  { key: "dns", label: "DNS records checklist", file: "DNS_CHECKLIST.md", gen: (d) => generateDnsChecklist(d) },
  { key: "twilio", label: "Twilio webhook checklist", file: "TWILIO_CHECKLIST.md", gen: (d) => generateTwilioWebhookChecklist(d) },
  { key: "resend", label: "Resend verification checklist", file: "RESEND_CHECKLIST.md", gen: (d) => generateResendChecklist(d) },
  { key: "stripe", label: "Stripe / payments checklist", file: "STRIPE_CHECKLIST.md", gen: (d) => generateStripeChecklist(d) },
  { key: "admin", label: "First-admin checklist", file: "FIRST_ADMIN.md", gen: generateFirstAdminChecklist },
  { key: "golive", label: "Go-live test plan", file: "GO_LIVE_TEST_PLAN.md", gen: (d) => generateGoLiveTestPlan(d) },
  { key: "readiness", label: "Deployment readiness summary", file: "READINESS.md", gen: generateReadinessSummary },
];

const OutputPanel = ({ data, secrets, readiness }) => {
  const grouped = evaluateGroups(data, secrets);
  // Launch readiness score (0–100) is driven purely by REQUIRED checklist
  // completion — same source as the per-group counts, so they never disagree.
  const score = grouped.percent;
  const missingCount = grouped.reqTotal - grouped.reqDone;
  const ready = missingCount === 0;
  const missingByGroup = grouped.groups
    .map((g) => ({ title: g.title, items: g.items.filter((i) => !i.optional && !i.ok).map((i) => i.label) }))
    .filter((g) => g.items.length > 0);
  const scoreTone = ready ? "#10B981" : score >= 60 ? "#92400E" : "#B91C1C";
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };
  return (
    <div className="space-y-4" data-testid="provisioning-output">
      <div className="border border-border p-4 bg-[#FAFAF7]" data-testid="provisioning-readiness">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="label-mini text-muted-foreground">LAUNCH READINESS</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-4xl font-bold tabular-nums" style={{ color: scoreTone }} data-testid="launch-readiness-score">{score}</span>
              <span className="text-sm text-muted-foreground">/ 100</span>
            </div>
          </div>
          <span className="label-mini font-bold whitespace-nowrap" style={{ color: scoreTone }}>{grouped.reqDone}/{grouped.reqTotal} required</span>
        </div>
        <div className="h-2 bg-secondary border border-border overflow-hidden mb-4">
          <div className="h-full transition-all duration-300" style={{ width: `${score}%`, background: scoreTone }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {grouped.groups.map((g) => (
            <div key={g.key} data-testid={`readiness-group-${g.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="label-mini text-foreground font-bold">{g.title}</div>
                <span className="label-mini text-muted-foreground">{g.done}/{g.total}</span>
              </div>
              <ul className="space-y-1">
                {g.items.map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm" data-testid={`readiness-item-${g.key}-${i}`}>
                    {it.ok
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                      : <span className={`w-3.5 h-3.5 flex-shrink-0 border ${it.optional ? "border-dashed border-muted-foreground" : "border-[#92400E]"} rounded-full`} />}
                    <span className={it.ok ? "text-foreground" : "text-muted-foreground"}>
                      {it.label}{it.optional && <span className="text-[10px] uppercase tracking-wide text-muted-foreground ml-1">opt</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {!ready && (
        <div className="border border-[#F59E0B] bg-[#FFFBEB] p-3" data-testid="readiness-missing-banner">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-[#92400E] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#92400E]" data-testid="readiness-missing-count">
                {missingCount} required item{missingCount === 1 ? "" : "s"} still missing before launch
              </div>
              <div className="text-xs text-[#92400E] mt-0.5 mb-2">
                You can still export the pack now — finish these before going live.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {missingByGroup.map((g) => (
                  <div key={g.title} data-testid={`readiness-missing-group-${slugify(g.title)}`}>
                    <div className="label-mini text-[#92400E] font-bold mb-1">{g.title}</div>
                    <ul className="space-y-0.5">
                      {g.items.map((label, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-[#7C2D12]">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-[#92400E] flex-shrink-0" />
                          <span>{label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => downloadText(`${slugify(data.company_name)}_PROVISIONING_PACK.md`, generateProvisioningPack(data, secrets))} className="bg-[#0A0A0A] hover:bg-black text-white flex items-center gap-1.5" data-testid="provisioning-download-pack">
          <Download className="w-4 h-4" /> {ready ? "Download full pack (.md)" : "Export anyway (.md)"}
        </Button>
      </div>

      <div className="space-y-3">
        {OUTPUTS.map((o) => {
          const text = o.gen(data, secrets);
          return (
            <div key={o.key} className="border border-border" data-testid={`provisioning-output-${o.key}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-secondary border-b border-border">
                <span className="text-sm font-bold">{o.label}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => copy(text, o.label)} className="h-7 px-2 flex items-center gap-1" data-testid={`provisioning-copy-${o.key}`}><Copy className="w-3.5 h-3.5" /> Copy</Button>
                  <Button variant="ghost" size="sm" onClick={() => downloadText(o.file, text)} className="h-7 px-2 flex items-center gap-1" data-testid={`provisioning-download-${o.key}`}><Download className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <pre className="text-[11px] leading-relaxed p-3 overflow-x-auto whitespace-pre-wrap font-mono-tab max-h-72 overflow-y-auto" data-testid={`provisioning-pre-${o.key}`}>{text}</pre>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OperatorProvisioningWizard;
